import PDFDocument from 'pdfkit/js/pdfkit.standalone.js';
import {
	COLOR_BLACK,
	COLOR_LINE,
	COL_HEADER_TEXT_SIZE,
	COL_HEIGHT,
	COL_TEXT_OPTIONS,
	COL_TEXT_SIZE,
	COL_TEXT_SIZE_SMALL,
	FONT,
	FONT_BOLD,
	FONT_SIZE_NORMAL,
	FOOTER_MARGIN,
	LINE_WEIGHT,
	MINIMUM_ROWS_PER_PAGE
} from '../variables';
import { fetchWebImage, isWebImage } from './image';
import { renderLine } from './line';
import { cmToPoint } from './units';

const IMAGE_WIDTH = cmToPoint(2.5);
const IMAGE_HEIGHT = cmToPoint(2);

export function getQuoteTableColWidths(pdf: PDFDocument, image: boolean) {
	const width = pdf.page.width - pdf.page.margins.left - pdf.page.margins.right;

	const colImage = image ? IMAGE_WIDTH : 0;
	const colInch = cmToPoint(1);
	const colQuantity = cmToPoint(1.4);
	const colPrice = cmToPoint(2);
	const colDiscount = cmToPoint(2);
	const colTotalPrice = cmToPoint(2);
	const colDescription = width - colImage - colInch - colQuantity - colPrice - colDiscount - colTotalPrice;

	return {
		colImage,
		colInch,
		colQuantity,
		colPrice,
		colDiscount,
		colTotalPrice,
		colDescription
	};
}

export function getPresaleTableColWidths(pdf: PDFDocument) {
	const width = pdf.page.width - pdf.page.margins.left - pdf.page.margins.right;

	const colQuantity = cmToPoint(1.4);
	const colTotalPrice = cmToPoint(2);
	const colDescription = cmToPoint(11);
	const colInch = width - colQuantity - colDescription - colTotalPrice;

	return {
		colInch,
		colQuantity,
		colTotalPrice,
		colDescription
	};
}

export async function renderQuoteTable(
	pdf: PDFDocument,
	header: {
		description: string;
		inch: string;
		quantity: string;
		price: string;
		discount: string;
		totalPrice: string;
	},
	items: {
		image: string;
		description: string;
		inch: string;
		quantity: string;
		price: string;
		discount: string;
		totalPrice: string;
		descriptions?: string[];
	}[]
) {
	const hasImages = items.filter(o => !!o.image).length > 0;

	const maxY = pdf.page.height - pdf.page.margins.bottom;

	// calculate size for total price
	pdf.fontSize(COL_TEXT_SIZE).font(FONT);

	const { colImage, colInch, colQuantity, colPrice, colDiscount, colTotalPrice, colDescription } = getQuoteTableColWidths(pdf, hasImages);

	pdf.y += cmToPoint(0.1);

	renderLine(pdf, COLOR_LINE, LINE_WEIGHT);

	pdf.y += cmToPoint(0.1);

	pdf.fillColor(COLOR_BLACK);

	async function row(
		header: boolean,
		image: string | null,
		description: string,
		inch: string,
		quantity: string,
		price: string,
		discount: string,
		totalPrice: string
	) {
		if (header) {
			pdf.fontSize(COL_HEADER_TEXT_SIZE).font(FONT_BOLD);
		} else {
			pdf.fontSize(COL_TEXT_SIZE).font(FONT);
		}

		const colHeight = COL_HEIGHT;

		// it should be place enough for some rows
		if (pdf.y + colHeight * MINIMUM_ROWS_PER_PAGE + FOOTER_MARGIN > maxY) {
			pdf.addPage();
		}

		const startX = pdf.x;
		const startY = pdf.y;
		let colX = startX;

		if (hasImages) {
			if (header) {
				pdf.text(description, colX, startY, {
					...COL_TEXT_OPTIONS,
					width: colDescription + colImage
				});

				colX += colImage;
			} else {
				try {
					const resolvedImage = image ? (isWebImage(image) ? await fetchWebImage(image) : image) : null;

					// + some gap
					pdf.image(resolvedImage, colX + 4, startY, {
						width: colImage,
						height: IMAGE_HEIGHT,
						align: 'center',
						valign: 'center',
						fit: [IMAGE_HEIGHT, IMAGE_HEIGHT]
					});
				} catch (err) {
					console.error(err);
				}

				pdf.text(description, (colX += colImage), startY, {
					...COL_TEXT_OPTIONS,
					width: colDescription
				});
			}
		} else {
			pdf.text(description, colX, startY, {
				...COL_TEXT_OPTIONS,
				width: colDescription
			});
		}

		pdf.text(inch, (colX += colDescription), startY, {
			...COL_TEXT_OPTIONS,
			width: colInch
		});
		pdf.text(quantity, (colX += colInch), startY, {
			...COL_TEXT_OPTIONS,
			width: colQuantity,
			align: 'right'
		});
		pdf.text(price, (colX += colQuantity), startY, {
			...COL_TEXT_OPTIONS,
			width: colPrice,
			align: 'right'
		});
		pdf.text(discount, (colX += colPrice), startY, {
			...COL_TEXT_OPTIONS,
			width: colDiscount,
			align: 'right'
		});
		pdf.text(totalPrice, (colX += colDiscount), startY, {
			...COL_TEXT_OPTIONS,
			width: colTotalPrice,
			align: 'right'
		});

		pdf.x = startX;
		pdf.y = startY + colHeight;
	}

	function rowDescription(description: string) {
		pdf.fontSize(COL_TEXT_SIZE_SMALL).font(FONT);

		const width = colDescription;

		const descriptionOptions = {
			...COL_TEXT_OPTIONS,
			lineGap: 1.5,
			height: undefined,
			width
		};

		descriptionOptions.height = pdf.heightOfString(description, descriptionOptions);

		const maxHeight = Math.max(COL_HEIGHT, descriptionOptions.height ?? 0);

		// it should be place enough for some rows
		if (pdf.y + maxHeight * MINIMUM_ROWS_PER_PAGE + FOOTER_MARGIN > maxY) {
			pdf.addPage();
		}

		const startX = pdf.x;
		const startY = pdf.y;
		const colX = startX + (hasImages ? colImage : 0);

		pdf.text(description, colX, startY, descriptionOptions);

		pdf.x = startX;
		pdf.y = startY + maxHeight;
	}

	await row(true, null, header.description, header.inch, header.quantity, header.price, header.discount, header.totalPrice);

	pdf.y += 2; // some gap after header

	for (const item of items) {
		pdf.y += 2; // some gap after each item
		const startY = pdf.y;

		await row(false, item.image, item.description, item.inch, item.quantity, item.price, item.discount, item.totalPrice);

		if (item.descriptions?.length) {
			pdf.y += 2; // some gap

			for (const description of item.descriptions) {
				rowDescription(description);
			}
		}

		// add image height if the row is too small
		if (item.image) {
			const height = IMAGE_HEIGHT - (pdf.y - startY) + 3; // + some gap

			if (height > 0) {
				pdf.y += height;
			}
		}
	}
}

export function renderPresaleTable(
	pdf: PDFDocument,
	header: {
		description: string;
		inch: string;
		quantity: string;
		totalPrice: string;
	},
	items: {
		description: string;
		inch: string;
		quantity: string;
		totalPrice: string;
		descriptions?: string[];
	}[]
) {
	const maxY = pdf.page.height - pdf.page.margins.bottom;

	// calculate size for total price
	pdf.fontSize(COL_TEXT_SIZE).font(FONT);

	const { colInch, colQuantity, colTotalPrice, colDescription } = getPresaleTableColWidths(pdf);

	pdf.y += cmToPoint(0.1);

	renderLine(pdf, COLOR_LINE, LINE_WEIGHT);

	pdf.y += cmToPoint(0.1);

	pdf.fillColor(COLOR_BLACK);

	function row(header: boolean, description: string, inch: string, quantity: string, totalPrice: string) {
		if (header) {
			pdf.fontSize(COL_HEADER_TEXT_SIZE).font(FONT_BOLD);
		} else {
			pdf.fontSize(COL_TEXT_SIZE).font(FONT);
		}

		const colHeight = COL_HEIGHT;

		// it should be place enough for some rows
		if (pdf.y + colHeight * MINIMUM_ROWS_PER_PAGE + FOOTER_MARGIN > maxY) {
			pdf.addPage();
		}

		const startX = pdf.x;
		const startY = pdf.y;
		let colX = startX;

		pdf.text(description, colX, startY, {
			...COL_TEXT_OPTIONS,
			width: colDescription
		});
		pdf.text(inch, (colX += colDescription), startY, {
			...COL_TEXT_OPTIONS,
			width: colInch,
			align: 'right'
		});
		pdf.text(quantity, (colX += colInch), startY, {
			...COL_TEXT_OPTIONS,
			width: colQuantity,
			align: 'right'
		});
		pdf.text(totalPrice, (colX += colQuantity), startY, {
			...COL_TEXT_OPTIONS,
			width: colTotalPrice,
			align: 'right'
		});

		pdf.x = startX;
		pdf.y = startY + colHeight;
	}

	function rowDescription(description: string) {
		pdf.fontSize(COL_TEXT_SIZE_SMALL).font(FONT);

		const width = colDescription;

		const descriptionOptions = {
			...COL_TEXT_OPTIONS,
			lineGap: 1.5,
			height: undefined,
			width
		};

		descriptionOptions.height = pdf.heightOfString(description, descriptionOptions);

		const maxHeight = Math.max(COL_HEIGHT, descriptionOptions.height ?? 0);

		// it should be place enough for some rows
		if (pdf.y + maxHeight * MINIMUM_ROWS_PER_PAGE + FOOTER_MARGIN > maxY) {
			pdf.addPage();
		}

		const startX = pdf.x;
		const startY = pdf.y;
		const colX = startX;

		pdf.text(description, colX, startY, descriptionOptions);

		pdf.x = startX;
		pdf.y = startY + maxHeight;
	}

	row(true, header.description, header.inch, header.quantity, header.totalPrice);

	pdf.y += 2; // some gap after header

	for (const item of items) {
		pdf.y += 2; // some gap after each item

		row(false, item.description, item.inch, item.quantity, item.totalPrice);

		if (item.descriptions?.length) {
			pdf.y += 2; // some gap

			for (const description of item.descriptions) {
				rowDescription(description);
			}
		}
	}
}

export function renderSummaryTable(pdf: PDFDocument, body: any, indent: number) {
	if (!body.summary?.length && !body.totalPrice) {
		return;
	}

	pdf.y += cmToPoint(0.1);

	renderLine(pdf, COLOR_LINE, LINE_WEIGHT);

	pdf.y += cmToPoint(0.1);

	// calculate size for total price
	pdf.fontSize(COL_TEXT_SIZE).font(FONT);

	// e.g. 1'000'000.00
	const colTotalPrice = pdf.widthOfString(`X'XXX'XXX.XX`, {
		align: 'right'
	});

	const maxY = pdf.page.height - pdf.page.margins.bottom;
	const width = pdf.page.width - pdf.page.margins.left - pdf.page.margins.right;
	const colText = width - indent - colTotalPrice;

	function row(text: string, totalPrice: string) {
		// it should be place enough for some rows
		if (pdf.y + COL_HEIGHT * MINIMUM_ROWS_PER_PAGE + FOOTER_MARGIN > maxY) {
			pdf.addPage();
		}

		const startX = pdf.x;
		const startY = pdf.y;

		let colX = startX + indent;

		pdf.text(text, colX, startY, {
			...COL_TEXT_OPTIONS,
			width: colText
		});
		pdf.text(totalPrice || '', (colX += colText), startY, {
			...COL_TEXT_OPTIONS,
			width: colTotalPrice,
			align: 'right'
		});

		pdf.x = startX;
		pdf.y = startY + COL_HEIGHT;
	}

	if (body.summary?.length) {
		for (const item of body.summary) {
			row(item.label, item.value);
		}
	}

	renderTotal(pdf, body, indent);
}

export function renderTotal(pdf: PDFDocument, body: any, indent: number) {
	if (!body.totalPrice) {
		return;
	}

	pdf.y += cmToPoint(0.1);

	renderLine(pdf, COLOR_LINE, LINE_WEIGHT * 2);

	pdf.y += cmToPoint(0.1);

	pdf.fontSize(FONT_SIZE_NORMAL).font(FONT_BOLD);

	// e.g. 1'000'000
	const colTotalPriceBig = pdf.widthOfString(`XXX X'XXX'XXX`, {
		align: 'right'
	});

	const maxY = pdf.page.height - pdf.page.margins.bottom;
	const width = pdf.page.width - pdf.page.margins.left - pdf.page.margins.right;

	const colTextBig = width - indent - colTotalPriceBig;

	// it should be place enough for some rows
	if (pdf.y + COL_HEIGHT * MINIMUM_ROWS_PER_PAGE + FOOTER_MARGIN > maxY) {
		pdf.addPage();
	}

	const startX = pdf.x;
	const startY = pdf.y;
	let colX = startX + indent;

	pdf.text(body.totalPrice.label, colX, startY, {
		...COL_TEXT_OPTIONS,
		width: colTextBig
	});
	pdf.text(body.totalPrice.value, (colX += colTextBig), startY, {
		...COL_TEXT_OPTIONS,
		width: colTotalPriceBig,
		align: 'right'
	});

	pdf.x = startX;
	pdf.y = startY + COL_HEIGHT;
}
